import React, { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";

interface Friend {
  id?: string;
  name: string;
  imageUrl: string;
  relationship: string;
}

export default function FriendsOfWattlenest() {
  const [friends, setFriends] = useState<Friend[]>([]);
  const [randomFriends, setRandomFriends] = useState<Friend[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch friends from Firestore
  useEffect(() => {
    const fetchFriends = async () => {
      setIsLoading(true);
      try {
        const friendsCollection = collection(db, "friends");
        const friendsSnapshot = await getDocs(friendsCollection);
        const friendsList = friendsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Friend[];
        setFriends(friendsList);

        // Select 6 random friends
        selectRandomFriends(friendsList);
      } catch (error) {
        console.error("Error fetching friends:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFriends();
  }, []);

  // Function to select 6 random friends
  const selectRandomFriends = (friendsList: Friend[]) => {
    if (friendsList.length <= 6) {
      // If we have 6 or fewer friends, show all of them
      setRandomFriends(friendsList);
    } else {
      // Create a copy of the array to avoid modifying the original
      const friendsCopy = [...friendsList];
      const selected: Friend[] = [];

      // Select 6 random friends
      for (let i = 0; i < 6; i++) {
        if (friendsCopy.length === 0) break;

        // Get a random index
        const randomIndex = Math.floor(Math.random() * friendsCopy.length);

        // Add the friend at that index to our selected array
        selected.push(friendsCopy[randomIndex]);

        // Remove that friend from the copy to avoid duplicates
        friendsCopy.splice(randomIndex, 1);
      }

      setRandomFriends(selected);
    }
  };

  // Function to select a new set of random friends
  const refreshRandomFriends = () => {
    selectRandomFriends(friends);
  };

  return (
    <div className="mx-auto max-w-7xl py-16">
      <div className="mx-auto max-w-7xl lg:text-left mb-12">
        <h2 className="text-base font-semibold leading-7 text-gold">
          Our Community
        </h2>
        <p className="mt-2 text-2xl font-bold tracking-tight text-gray-900">
          Friends of Wattlenest
        </p>
        <p className="mt-4 text-lg text-gray-600">
          Supporters, mentors, and advocates who share our vision.
        </p>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-gold border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
          <p className="mt-4 text-gray-600">Loading friends...</p>
        </div>
      ) : friends.length === 0 ? (
        <p className="text-center py-12 text-gray-600">No friends found.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-6">
            {randomFriends.map((friend) => (
              <div
                key={friend.id}
                className="relative overflow-hidden rounded-lg bg-white shadow-md transition-all hover:shadow-lg"
              >
                <div className="aspect-[4/4.5] relative">
                  <img
                    src={friend.imageUrl}
                    alt={friend.name}
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src =
                        "/placeholder-image.svg";
                    }}
                  />
                </div>
                <div className="p-2">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {friend.name}
                  </h3>
                  <p className="mt-1 text-xs text-gray-500">
                    {friend.relationship}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <button
              onClick={refreshRandomFriends}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gold hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold"
            >
              Show More
            </button>

            <a
              href="/friends"
              className="inline-flex items-center ml-4 px-4 py-2 border border-gold text-sm font-medium rounded-md shadow-sm text-gold bg-goldlight hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold"
            >
              View All Friends
            </a>
          </div>
        </>
      )}
    </div>
  );
}
